import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CreditCard, CheckCircle, XCircle, RefreshCw, Loader2, ArrowLeft, Banknote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpectatorPaymentScreen({
  gymnast,
  club,
  competition,
  spectatorCount,
  spectatorSettings,
  sessionTerminal, // Pre-selected terminal from RegistrationApp
  onPaymentComplete,
  onBack
}) {
  const [status, setStatus] = useState('INITIALIZING'); // INITIALIZING, CONNECTING, READY, PROCESSING, SUCCESS, FAILED, CANCELLED, TIMEOUT
  const [statusMessage, setStatusMessage] = useState('Connecting to card reader...');
  const [paymentId, setPaymentId] = useState(null);
  const [selectedTerminal, setSelectedTerminal] = useState('');
  const [showCashDialog, setShowCashDialog] = useState(false);
  const [cashReason, setCashReason] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const pollingRef = useRef(null);
  const startTimeRef = useRef(null);
  const MAX_POLL_TIME = 90000; // 90 seconds max

  // Calculate payment amount
  const includedSpectators = spectatorSettings?.includedSpectators || 0;
  const unitPrice = spectatorSettings?.extraSpectatorPriceGbp || 0;
  const extraSpectators = Math.max(0, spectatorCount - includedSpectators);
  const amount = extraSpectators * unitPrice;

  // Get terminals list
  const terminals = spectatorSettings?.terminals || [];

  // Use session terminal if provided, otherwise allow selection
  const effectiveTerminal = sessionTerminal?.terminalId || selectedTerminal;
  const terminalName = sessionTerminal?.name || terminals.find(t => t.terminalId === selectedTerminal)?.name || 'Terminal';

  // Initialize selected terminal
  useEffect(() => {
    if (sessionTerminal) {
      // Session terminal is pre-selected - use it immediately
      setSelectedTerminal(sessionTerminal.terminalId);
    } else if (terminals.length > 0 && !selectedTerminal) {
      // No session terminal - check for saved preference or use first
      const savedTerminal = localStorage.getItem('scorz_selected_terminal');
      if (savedTerminal && terminals.find(t => t.terminalId === savedTerminal)) {
        setSelectedTerminal(savedTerminal);
      } else {
        setSelectedTerminal(terminals[0].terminalId);
      }
    }
  }, [terminals, selectedTerminal, sessionTerminal]);

  // Track if payment initiation is in progress to prevent duplicates
  const initiatingRef = useRef(false);

  // Start payment when terminal is available
  useEffect(() => {
    if (effectiveTerminal && status === 'INITIALIZING' && !initiatingRef.current) {
      initiatingRef.current = true;
      initiatePayment();
    }
    // Cleanup polling on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [effectiveTerminal]);

  const initiatePayment = async () => {
    if (!effectiveTerminal) {
      toast.error('Please select a terminal');
      return;
    }

    // Save terminal preference (only if not using session terminal)
    if (!sessionTerminal) {
      localStorage.setItem('scorz_selected_terminal', effectiveTerminal);
    }

    setStatus('CONNECTING');
    setStatusMessage('Preparing card reader...');
    startTimeRef.current = Date.now();

    try {
      // Create the payment directly (backend handles terminal clearing)
      const response = await axios.post(`${API}/registration/spectator-payment/create`, {
        competitionId: competition.id,
        gymnastId: gymnast.id,
        clubId: club.id,
        clubName: club.name,
        gymnastName: `${gymnast.preferredFirstName || gymnast.firstName} ${gymnast.preferredLastName || gymnast.lastName}`,
        includedSpectators,
        selectedSpectators: spectatorCount,
        extraSpectators,
        unitPrice,
        amount,
        terminalId: effectiveTerminal,
        terminalName: terminalName
      });

      if (response.data.status === 'SUCCESS') {
        // Payment already completed (idempotency)
        setStatus('SUCCESS');
        setStatusMessage('Payment already completed!');
        setTimeout(() => onPaymentComplete(response.data), 1500);
        return;
      }

      setPaymentId(response.data.paymentId);
      setStatus('READY');
      setStatusMessage('Ready - ask customer to tap/insert card');

      // Start polling for status
      startPolling(response.data.paymentId);

    } catch (error) {
      console.error('Payment initiation failed:', error);
      setStatus('FAILED');
      setStatusMessage(error.response?.data?.detail || 'Failed to connect to terminal');
    }
  };

  const startPolling = useCallback((pid) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(async () => {
      // Check for timeout
      if (Date.now() - startTimeRef.current > MAX_POLL_TIME) {
        clearInterval(pollingRef.current);
        setStatus('TIMEOUT');
        setStatusMessage('No response from reader - payment timed out');
        return;
      }

      try {
        const response = await axios.get(`${API}/registration/spectator-payment/${pid}/status`);
        const data = response.data;

        switch (data.status) {
          case 'SUCCESS':
            clearInterval(pollingRef.current);
            setStatus('SUCCESS');
            setStatusMessage('Payment approved!');
            toast.success('Payment successful!');
            setTimeout(() => onPaymentComplete(data), 1500);
            break;
          
          case 'FAILED':
            clearInterval(pollingRef.current);
            setStatus('FAILED');
            setStatusMessage(data.errorMessage || 'Payment failed');
            break;
          
          case 'CANCELLED':
            clearInterval(pollingRef.current);
            setStatus('CANCELLED');
            setStatusMessage('Payment was cancelled');
            break;
          
          case 'TIMEOUT':
            clearInterval(pollingRef.current);
            setStatus('TIMEOUT');
            setStatusMessage('Payment timed out');
            break;
          
          case 'PENDING':
          default:
            // Update status message based on elapsed time
            const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
            if (elapsed > 30) {
              setStatus('PROCESSING');
              setStatusMessage('Processing payment...');
            } else if (elapsed > 10) {
              setStatus('PROCESSING');
              setStatusMessage('Waiting for card tap/insert...');
            }
            break;
        }
      } catch (error) {
        console.error('Status poll error:', error);
        // Don't fail on poll errors, just continue
      }
    }, 1000); // Poll every second
  }, [onPaymentComplete]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setStatus('INITIALIZING');
    setStatusMessage('Connecting to card reader...');
    setPaymentId(null);
    initiatePayment();
  };

  const handleMarkAsCash = async () => {
    if (!cashReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('reason', cashReason);

      await axios.post(`${API}/registration/spectator-payment/${paymentId}/mark-cash`, formData);
      toast.success('Marked as cash payment');
      setShowCashDialog(false);
      onPaymentComplete({ status: 'CASH', paymentId });
    } catch (error) {
      console.error('Failed to mark as cash:', error);
      toast.error('Failed to mark as cash');
    }
  };

  const handleCancel = async () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    if (paymentId) {
      try {
        await axios.post(`${API}/registration/spectator-payment/${paymentId}/cancel`);
      } catch (error) {
        console.error('Failed to cancel payment:', error);
      }
    }

    onBack();
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-24 h-24 text-green-600" />;
      case 'FAILED':
      case 'CANCELLED':
      case 'TIMEOUT':
        return <XCircle className="w-24 h-24 text-red-600" />;
      case 'CONNECTING':
      case 'PROCESSING':
        return <Loader2 className="w-24 h-24 text-black animate-spin" />;
      case 'READY':
        return <CreditCard className="w-24 h-24 text-black animate-pulse" />;
      default:
        return <RefreshCw className="w-24 h-24 text-gray-400 animate-spin" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-50 border-green-500';
      case 'FAILED':
      case 'CANCELLED':
      case 'TIMEOUT':
        return 'bg-red-50 border-red-500';
      case 'READY':
        return 'bg-blue-50 border-blue-500';
      default:
        return 'bg-gray-50 border-gray-300';
    }
  };

  return (
    <Card data-testid="spectator-payment-screen">
      <CardHeader className="text-center pb-2">
        <CardTitle className="text-2xl flex items-center justify-center gap-2">
          <CreditCard className="w-6 h-6" />
          Extra Spectator Payment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Gymnast Info */}
        <div className="bg-gray-100 p-4 rounded-lg text-center">
          <p className="text-xl font-bold">
            {gymnast.preferredFirstName || gymnast.firstName} {gymnast.preferredLastName || gymnast.lastName}
          </p>
          <p className="text-gray-600">{club.name}</p>
        </div>

        {/* Spectator Breakdown */}
        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Included Spectators</p>
            <p className="text-2xl font-bold">{includedSpectators}</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Selected Spectators</p>
            <p className="text-2xl font-bold">{spectatorCount}</p>
          </div>
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600">Extra Spectators</p>
            <p className="text-2xl font-bold text-orange-700">{extraSpectators}</p>
          </div>
          <div className="p-3 bg-black text-white rounded-lg">
            <p className="text-sm text-gray-300">Amount Due</p>
            <p className="text-3xl font-bold">£{amount.toFixed(2)}</p>
          </div>
        </div>

        {/* Terminal Selection - Only show if no session terminal and multiple terminals */}
        {!sessionTerminal && terminals.length > 1 && status !== 'SUCCESS' && (
          <div>
            <Label>Card Reader</Label>
            <Select
              value={selectedTerminal}
              onValueChange={(val) => {
                setSelectedTerminal(val);
                localStorage.setItem('scorz_selected_terminal', val);
              }}
              disabled={status === 'PROCESSING'}
            >
              <SelectTrigger className="mt-1" data-testid="terminal-select">
                <SelectValue placeholder="Select terminal..." />
              </SelectTrigger>
              <SelectContent>
                {terminals.map((terminal) => (
                  <SelectItem key={terminal.terminalId} value={terminal.terminalId}>
                    {terminal.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Show which terminal is being used (read-only info) */}
        {sessionTerminal && (
          <div className="bg-gray-50 p-3 rounded-lg text-center">
            <p className="text-sm text-gray-600">Using Terminal</p>
            <p className="font-semibold">{sessionTerminal.name}</p>
          </div>
        )}

        {/* Status Display */}
        <div className={`p-8 rounded-lg border-4 text-center ${getStatusColor()}`} data-testid="payment-status">
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>
          <p className="text-xl font-semibold" data-testid="status-message">
            {statusMessage}
          </p>
          {status === 'READY' && (
            <p className="text-sm text-gray-600 mt-2">
              Present the card reader to the customer
            </p>
          )}
          {status === 'CANCELLED' && (
            <p className="text-sm text-red-600 mt-2 font-medium">
              If the terminal is still active, press the red X or trash icon on the terminal to cancel
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* CANCEL BUTTON - Big, red, prominent during active payment */}
          {(status === 'CONNECTING' || status === 'READY' || status === 'PROCESSING') && (
            <Button
              onClick={handleCancel}
              className="w-full h-16 text-xl font-bold bg-red-600 hover:bg-red-700 text-white"
              data-testid="payment-cancel-btn"
            >
              <XCircle className="w-6 h-6 mr-2" />
              CANCEL PAYMENT
            </Button>
          )}

          {/* Back Button - shown after cancel/fail */}
          {status !== 'SUCCESS' && status !== 'CONNECTING' && status !== 'READY' && status !== 'PROCESSING' && (
            <Button
              onClick={onBack}
              variant="outline"
              className="w-full h-14 text-lg"
              data-testid="payment-back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}

          {/* Retry Button - Visible on failure states */}
          {(status === 'FAILED' || status === 'CANCELLED' || status === 'TIMEOUT') && (
            <Button
              onClick={handleRetry}
              className="w-full h-14 text-lg bg-black hover:bg-gray-800"
              data-testid="payment-retry-btn"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Retry Payment
            </Button>
          )}

          {/* Mark as Cash - Visible on failure states or timeout */}
          {(status === 'FAILED' || status === 'CANCELLED' || status === 'TIMEOUT') && (
            <Button
              onClick={() => setShowCashDialog(true)}
              variant="outline"
              className="w-full h-14 text-lg border-orange-500 text-orange-600 hover:bg-orange-50"
              data-testid="payment-cash-btn"
            >
              <Banknote className="w-5 h-5 mr-2" />
              Mark as Cash
            </Button>
          )}
        </div>

        {/* Cash Payment Dialog */}
        <AlertDialog open={showCashDialog} onOpenChange={setShowCashDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark as Cash Payment</AlertDialogTitle>
              <AlertDialogDescription>
                Please enter a reason for accepting cash instead of card payment.
                This will be logged for audit purposes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label>Reason</Label>
              <Input
                value={cashReason}
                onChange={(e) => setCashReason(e.target.value)}
                placeholder="e.g., Card reader offline, customer preference"
                className="mt-2"
                data-testid="cash-reason-input"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setCashReason('')}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleMarkAsCash}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-cash-btn"
              >
                Confirm Cash Payment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
